import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { organizationsApi } from '@/lib/api';
import type {
  Label,
  OrganizationWithRole,
  OrganizationDetails,
  OrganizationMemberDetails,
  OrganizationInvitationRow,
  RoleDefinition,
  RolePermissions,
} from '@/lib/api';
import { notifyError } from '@/stores/toast.store';
import { useAuthStore } from '@/stores/auth.store';

type LabelType = 'team' | 'project' | 'task' | 'list' | 'general';

export const orgKeys = {
  all: ['organizations'] as const,
  detail: (id: string) => ['organizations', id] as const,
  members: (id: string) => ['organizations', id, 'members'] as const,
  invitations: (id: string) => ['organizations', id, 'invitations'] as const,
  roles: (id: string) => ['organizations', id, 'roles'] as const,
  labels: (id: string) => ['organizations', id, 'labels'] as const,
  allLabels: (id: string, labelType?: LabelType) =>
    ['organizations', id, 'all-labels', labelType ?? 'all'] as const,
};

export function useOrganizations() {
  return useQuery({ queryKey: orgKeys.all, queryFn: organizationsApi.list });
}

export function useOrganization(id: string) {
  return useQuery({
    queryKey: orgKeys.detail(id),
    queryFn: () => organizationsApi.get(id),
    enabled: !!id,
  });
}

/** All labels in the active org — the single query replacing the old
 * client-side per-team label union on DashboardPage/ListBoard. */
export function useOrgLabels(organizationId: string | null, labelType?: LabelType) {
  return useQuery({
    queryKey: orgKeys.allLabels(organizationId ?? '', labelType),
    queryFn: () => organizationsApi.allLabels(organizationId!, labelType),
    enabled: !!organizationId,
  });
}

const OWNER_PERMS: RolePermissions = {
  view: true,
  edit: true,
  manageMembers: true,
  manageSettings: true,
};

export function useCreateOrganization() {
  const qc = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  return useMutation({
    mutationFn: (data: { name: string; description?: string }) => organizationsApi.create(data),
    onMutate: async (data) => {
      await qc.cancelQueries({ queryKey: orgKeys.all });
      const previous = qc.getQueryData<OrganizationWithRole[]>(orgKeys.all);
      const now = new Date();
      const optimistic: OrganizationWithRole = {
        id: `temp-${Date.now()}`,
        name: data.name,
        description: data.description ?? null,
        createdById: currentUser?.id ?? '',
        ownerId: currentUser?.id ?? '',
        createdAt: now,
        updatedAt: now,
        myRoleId: '',
        myRoleName: 'Owner',
        myPermissions: OWNER_PERMS,
      };
      qc.setQueryData<OrganizationWithRole[]>(orgKeys.all, (old) => [...(old ?? []), optimistic]);
      return { previous };
    },
    onError: (err, _data, context) => {
      if (context?.previous) qc.setQueryData(orgKeys.all, context.previous);
      notifyError(err, 'Failed to create organization.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: orgKeys.all }),
  });
}

export function useUpdateOrganization(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<{ name: string; description: string }>) =>
      organizationsApi.update(id, data),
    onMutate: async (data) => {
      await qc.cancelQueries({ queryKey: orgKeys.all });
      const previousList = qc.getQueryData<OrganizationWithRole[]>(orgKeys.all);
      qc.setQueryData<OrganizationWithRole[]>(orgKeys.all, (old) =>
        old?.map((o) => (o.id === id ? { ...o, ...data, updatedAt: new Date() } : o)),
      );
      return { previousList };
    },
    onError: (err, _data, context) => {
      if (context?.previousList) qc.setQueryData(orgKeys.all, context.previousList);
      notifyError(err, 'Failed to update organization.');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: orgKeys.all });
      qc.invalidateQueries({ queryKey: orgKeys.detail(id) });
    },
  });
}

export function useDeleteOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationsApi.delete(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: orgKeys.all });
      const previous = qc.getQueryData<OrganizationWithRole[]>(orgKeys.all);
      qc.setQueryData<OrganizationWithRole[]>(orgKeys.all, (old) =>
        old?.filter((o) => o.id !== id),
      );
      return { previous };
    },
    onError: (err, _id, context) => {
      if (context?.previous) qc.setQueryData(orgKeys.all, context.previous);
      notifyError(err, 'Failed to delete organization.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: orgKeys.all }),
  });
}

export function useTransferOrgOwnership(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (newOwnerId: string) => organizationsApi.transferOwnership(orgId, newOwnerId),
    onMutate: async (newOwnerId) => {
      await qc.cancelQueries({ queryKey: orgKeys.detail(orgId) });
      const previousDetail = qc.getQueryData<OrganizationDetails>(orgKeys.detail(orgId));

      qc.setQueryData<OrganizationDetails>(orgKeys.detail(orgId), (old) =>
        old ? { ...old, ownerId: newOwnerId } : old,
      );

      return { previousDetail };
    },
    onError: (err, _newOwnerId, context) => {
      if (context?.previousDetail) qc.setQueryData(orgKeys.detail(orgId), context.previousDetail);
      notifyError(err, 'Failed to transfer ownership.');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: orgKeys.detail(orgId) });
      qc.invalidateQueries({ queryKey: orgKeys.members(orgId) });
      qc.invalidateQueries({ queryKey: orgKeys.all });
    },
  });
}

// ── Members ─────────────────────────────────────────────────────────────────

export function useOrgMembers(id: string) {
  return useQuery({
    queryKey: orgKeys.members(id),
    queryFn: () => organizationsApi.listMembers(id),
    enabled: !!id,
  });
}

export function useUpdateOrgMemberRole(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      organizationsApi.updateMemberRole(orgId, userId, roleId),
    onMutate: async ({ userId, roleId }) => {
      await qc.cancelQueries({ queryKey: orgKeys.members(orgId) });
      const previous = qc.getQueryData<OrganizationMemberDetails[]>(orgKeys.members(orgId));
      qc.setQueryData<OrganizationMemberDetails[]>(orgKeys.members(orgId), (old) =>
        old?.map((m) => (m.userId === userId ? { ...m, roleId } : m)),
      );
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) qc.setQueryData(orgKeys.members(orgId), context.previous);
      notifyError(err, 'Failed to change the member role.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: orgKeys.members(orgId) }),
  });
}

export function useRemoveOrgMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => organizationsApi.removeMember(orgId, userId),
    onMutate: async (userId) => {
      await qc.cancelQueries({ queryKey: orgKeys.members(orgId) });
      const previous = qc.getQueryData<OrganizationMemberDetails[]>(orgKeys.members(orgId));
      qc.setQueryData<OrganizationMemberDetails[]>(orgKeys.members(orgId), (old) =>
        old?.filter((m) => m.userId !== userId),
      );
      return { previous };
    },
    onError: (err, _userId, context) => {
      if (context?.previous) qc.setQueryData(orgKeys.members(orgId), context.previous);
      notifyError(err, 'Failed to remove the member.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: orgKeys.members(orgId) }),
  });
}

// ── Invitations ─────────────────────────────────────────────────────────────

export function useOrgInvitations(orgId: string, enabled = true) {
  return useQuery({
    queryKey: orgKeys.invitations(orgId),
    queryFn: () => organizationsApi.listInvitations(orgId),
    enabled: !!orgId && enabled,
  });
}

export function useCreateOrgInvitation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { email?: string; roleId: string; channel?: 'email' | 'telegram' }) =>
      organizationsApi.createInvitation(orgId, data),
    onMutate: async (data) => {
      await qc.cancelQueries({ queryKey: orgKeys.invitations(orgId) });
      const previous = qc.getQueryData<OrganizationInvitationRow[]>(orgKeys.invitations(orgId));
      const optimistic: OrganizationInvitationRow = {
        id: `temp-${Date.now()}`,
        email: data.email ?? null,
        channel: data.channel ?? 'email',
        roleId: data.roleId,
        roleName: '',
        token: '',
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        createdAt: new Date().toISOString(),
        inviterName: '',
      };
      qc.setQueryData<OrganizationInvitationRow[]>(orgKeys.invitations(orgId), (old) => [
        optimistic,
        ...(old ?? []),
      ]);
      return { previous };
    },
    onError: (err, _data, context) => {
      if (context?.previous) qc.setQueryData(orgKeys.invitations(orgId), context.previous);
      notifyError(err, 'Failed to send the invitation.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: orgKeys.invitations(orgId) }),
  });
}

export function useRevokeOrgInvitation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => organizationsApi.revokeInvitation(orgId, inviteId),
    onMutate: async (inviteId) => {
      await qc.cancelQueries({ queryKey: orgKeys.invitations(orgId) });
      const previous = qc.getQueryData<OrganizationInvitationRow[]>(orgKeys.invitations(orgId));
      qc.setQueryData<OrganizationInvitationRow[]>(orgKeys.invitations(orgId), (old) =>
        old?.filter((i) => i.id !== inviteId),
      );
      return { previous };
    },
    onError: (err, _inviteId, context) => {
      if (context?.previous) qc.setQueryData(orgKeys.invitations(orgId), context.previous);
      notifyError(err, 'Failed to revoke the invitation.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: orgKeys.invitations(orgId) }),
  });
}

// ── Roles ───────────────────────────────────────────────────────────────────

export function useOrgRoles(orgId: string) {
  return useQuery({
    queryKey: orgKeys.roles(orgId),
    queryFn: () => organizationsApi.listRoles(orgId),
    enabled: !!orgId,
  });
}

export function useCreateOrgRole(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: { name: string; permissions: RolePermissions; sortOrder?: number }) =>
      organizationsApi.createRole(orgId, dto),
    onMutate: async (dto) => {
      await qc.cancelQueries({ queryKey: orgKeys.roles(orgId) });
      const previous = qc.getQueryData<RoleDefinition[]>(orgKeys.roles(orgId));
      const optimistic: RoleDefinition = {
        id: `temp-${Date.now()}`,
        ownerType: 'organization',
        ownerId: orgId,
        name: dto.name,
        description: null,
        permissions: dto.permissions,
        sortOrder: dto.sortOrder ?? 0,
        inheritableForMembers: true,
        createdAt: new Date(),
      };
      qc.setQueryData<RoleDefinition[]>(orgKeys.roles(orgId), (old) => [
        ...(old ?? []),
        optimistic,
      ]);
      return { previous };
    },
    onError: (err, _dto, context) => {
      if (context?.previous) qc.setQueryData(orgKeys.roles(orgId), context.previous);
      notifyError(err, 'Failed to create the role.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: orgKeys.roles(orgId) }),
  });
}

export function useUpdateOrgRole(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roleId,
      ...dto
    }: { roleId: string } & Partial<{
      name: string;
      permissions: RolePermissions;
      sortOrder: number;
    }>) => organizationsApi.updateRole(orgId, roleId, dto),
    onMutate: async ({ roleId, ...dto }) => {
      await qc.cancelQueries({ queryKey: orgKeys.roles(orgId) });
      const previous = qc.getQueryData<RoleDefinition[]>(orgKeys.roles(orgId));
      qc.setQueryData<RoleDefinition[]>(orgKeys.roles(orgId), (old) =>
        old?.map((r) => (r.id === roleId ? { ...r, ...dto } : r)),
      );
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) qc.setQueryData(orgKeys.roles(orgId), context.previous);
      notifyError(err, 'Failed to update the role.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: orgKeys.roles(orgId) }),
  });
}

export function useDeleteOrgRole(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => organizationsApi.deleteRole(orgId, roleId),
    onMutate: async (roleId) => {
      await qc.cancelQueries({ queryKey: orgKeys.roles(orgId) });
      const previous = qc.getQueryData<RoleDefinition[]>(orgKeys.roles(orgId));
      qc.setQueryData<RoleDefinition[]>(orgKeys.roles(orgId), (old) =>
        old?.filter((r) => r.id !== roleId),
      );
      return { previous };
    },
    onError: (err, _roleId, context) => {
      if (context?.previous) qc.setQueryData(orgKeys.roles(orgId), context.previous);
      notifyError(err, 'Failed to delete the role.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: orgKeys.roles(orgId) }),
  });
}

// ── Org-owned labels ────────────────────────────────────────────────────────

export function useOrgOwnedLabels(orgId: string, labelType?: LabelType) {
  return useQuery({
    queryKey: [...orgKeys.labels(orgId), labelType ?? 'all'],
    queryFn: () => organizationsApi.listLabels(orgId, labelType),
    enabled: !!orgId,
  });
}

function invalidateOrgLabelQueries(qc: ReturnType<typeof useQueryClient>, orgId: string) {
  qc.invalidateQueries({ queryKey: orgKeys.labels(orgId) });
  qc.invalidateQueries({ queryKey: ['organizations', orgId, 'all-labels'] });
}

export function useCreateOrgLabel(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: {
      labelType: LabelType;
      name: string;
      color: string;
      description?: string;
    }) => organizationsApi.createLabel(orgId, dto),
    onMutate: async (dto) => {
      const key = [...orgKeys.labels(orgId), 'all'];
      await qc.cancelQueries({ queryKey: orgKeys.labels(orgId) });
      const previous = qc.getQueryData<Label[]>(key);
      const now = new Date();
      const optimistic: Label = {
        id: `temp-${Date.now()}`,
        ownerType: 'organization',
        ownerId: orgId,
        organizationId: orgId,
        labelType: dto.labelType,
        name: dto.name,
        color: dto.color,
        description: dto.description ?? null,
        createdById: '',
        createdAt: now,
        updatedAt: now,
      };
      qc.setQueryData<Label[]>(key, (old) => [...(old ?? []), optimistic]);
      return { previous, key };
    },
    onError: (err, _dto, context) => {
      if (context?.previous) qc.setQueryData(context.key, context.previous);
      notifyError(err, 'Failed to create label.');
    },
    onSettled: () => invalidateOrgLabelQueries(qc, orgId),
  });
}

export function useUpdateOrgLabel(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      labelId,
      ...dto
    }: { labelId: string } & Partial<{ name: string; color: string; description: string }>) =>
      organizationsApi.updateLabel(orgId, labelId, dto),
    onMutate: async ({ labelId, ...dto }) => {
      const key = [...orgKeys.labels(orgId), 'all'];
      await qc.cancelQueries({ queryKey: orgKeys.labels(orgId) });
      const previous = qc.getQueryData<Label[]>(key);
      qc.setQueryData<Label[]>(key, (old) =>
        old?.map((l) => (l.id === labelId ? { ...l, ...dto } : l)),
      );
      return { previous, key };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) qc.setQueryData(context.key, context.previous);
      notifyError(err, 'Failed to update label.');
    },
    onSettled: () => invalidateOrgLabelQueries(qc, orgId),
  });
}

export function useDeleteOrgLabel(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (labelId: string) => organizationsApi.deleteLabel(orgId, labelId),
    onMutate: async (labelId) => {
      const key = [...orgKeys.labels(orgId), 'all'];
      await qc.cancelQueries({ queryKey: orgKeys.labels(orgId) });
      const previous = qc.getQueryData<Label[]>(key);
      qc.setQueryData<Label[]>(key, (old) => old?.filter((l) => l.id !== labelId));
      return { previous, key };
    },
    onError: (err, _labelId, context) => {
      if (context?.previous) qc.setQueryData(context.key, context.previous);
      notifyError(err, 'Failed to delete label.');
    },
    onSettled: () => invalidateOrgLabelQueries(qc, orgId),
  });
}
